import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ART } from '~/deck2/case/art';
import { COPY, DEPTHS, letterOf, SHAPES, USES, type Answer, type OwnQuestion } from '~/deck2/case/config';
import { catchCard, liftCard, reducedMotion, useMakeRoom, type CaughtCard } from '~/deck2/case/motion';
import { OwnWords } from '~/deck2/case/OwnWords';
import { ScreenSwap, type Direction } from '~/deck2/case/ScreenSwap';
import { Chip, OptionCard, theme } from '~/deck2/ui';
import caseStyles from '~/deck2/case/case.module.css';
import landing from '../landing.module.css';
import { Cursor, Stage } from './Stage';
import styles from './pick.module.css';

/** The app's first three questions, in its order. */
type Screen = 'about' | 'use' | 'depth';
const SCREENS: Screen[] = ['about', 'use', 'depth'];
const ANSWERS: Record<Screen, readonly Answer<string>[]> = { about: SHAPES, use: USES, depth: DEPTHS };
/** The questions with "Something else", and what it stands for when it was said in one's own words. */
type Said = Extract<Screen, OwnQuestion>;
const OWN: Record<Said, string> = { about: 'other', use: 'elsewhere' };
const sayable = (question: Screen): question is Said => question in OWN;
const LAST = SCREENS[SCREENS.length - 1];

/**
 * The tour, the way the app plays: the cursor comes in and picks "A problem I found", the card turns into its
 * chip at the top and "Where will you use it?" comes in; it picks "In an interview", and then "A deep dive". Each
 * leg: on its way (go), on the card, which acts its drawing out (on), press, picked. Then the cursor leaves (13).
 */
const MARKS = [600, 1600, 2250, 2400, 3300, 4300, 4950, 5100, 6000, 7000, 7650, 7800, 8600];
const LEGS = [
  { screen: 'about', card: 'problem', go: 1, on: 2, press: 3, pick: 4 },
  { screen: 'use', card: 'interview', go: 5, on: 6, press: 7, pick: 8 },
  { screen: 'depth', card: 'deep_dive', go: 9, on: 10, press: 11, pick: 12 },
] as const;
const GONE = 13;
/** How wide the scene's canvas may grow, in canvas units. */
const WIDE = 820;
const ENTER_AT: [number, number] = [600, 470];

type Picks = Record<Screen, { id: string; words?: string } | null>;
/** Where the tour ends, and the frame reduced motion shows. */
const ENDED: Picks = { about: { id: 'problem' }, use: { id: 'interview' }, depth: { id: 'deep_dive' } };

type Spots = Partial<Record<Screen, Record<string, [number, number]>>>;

/** An element's place inside `root`, in root's own pixels: transforms in between (a screen arriving) don't move it. */
function offsetIn(el: HTMLElement, root: HTMLElement): [number, number] {
  let x = 0;
  let y = 0;
  for (let at: HTMLElement | null = el; at && at !== root; at = at.offsetParent as HTMLElement | null) {
    x += at.offsetLeft;
    y += at.offsetTop;
  }
  return [x, y];
}

const cardsOn = (board: HTMLElement | null, screen: Screen) =>
  Array.from(board?.querySelectorAll<HTMLElement>(`[data-screen="${screen}"] [data-grid] > button`) ?? []);

/**
 * Where the cursor goes for each card on a screen, in canvas units: its tip a little right of and below the
 * card's middle. Measured, because the cards are laid out by the start flow's own grid, which changes with the
 * scene's width.
 */
function measure(board: HTMLElement, screen: Screen): Record<string, [number, number]> {
  const canvas = board.offsetParent as HTMLElement | null;
  if (!canvas) return {};
  const canvasBox = canvas.getBoundingClientRect();
  const boardBox = board.getBoundingClientRect();
  const toCanvas = canvasBox.width / canvas.offsetWidth;
  const boardScale = boardBox.width / board.offsetWidth;
  const left = (boardBox.left - canvasBox.left) / toCanvas;
  const top = (boardBox.top - canvasBox.top) / toCanvas;
  const spots: Record<string, [number, number]> = {};
  cardsOn(board, screen).forEach((card, i) => {
    // A card a phone leaves out has no place.
    if (!card.offsetParent) return;
    const [x, y] = offsetIn(card, board);
    const tipX = x + card.offsetWidth * 0.62;
    const tipY = y + card.offsetHeight * 0.64;
    spots[ANSWERS[screen][i].id] = [left + (tipX * boardScale) / toCanvas - 7, top + (tipY * boardScale) / toCanvas - 7];
  });
  return spots;
}

export function PickScene({ label }: { label: string }) {
  return (
    // Eight cards four across: wider than the other scenes where the page has the room, so each title fits its card.
    <Stage label={label} marks={MARKS} wide={WIDE} className={styles.scene}>
      {(step) => <Tour step={step} />}
    </Stage>
  );
}

/**
 * The app's own first three questions, their cards, chips and screen changes exactly as "Start your case study"
 * draws them, scaled into the scene. Once the person touches it, the tour stops and it is theirs: a card answers
 * and the next question comes, a chip goes back, "Something else" opens its box.
 */
function Tour({ step }: { step: number }) {
  const board = useRef<HTMLDivElement>(null);
  const trail = useRef<HTMLElement>(null);
  useMakeRoom(trail);
  const [screen, setScreen] = useState<Screen>('about');
  const [direction, setDirection] = useState<Direction>('forward');
  const [picks, setPicks] = useState<Picks>({ about: null, use: null, depth: null });
  const [spots, setSpots] = useState<Spots>({});
  const [touched, setTouched] = useState(false);
  const [still, setStill] = useState(false);
  const [lift, setLift] = useState<{ card: CaughtCard; chip: Screen } | null>(null);
  // "Something else", while its box is open: the card it grows out of, on which question.
  const [own, setOwn] = useState<{ from: DOMRect; question: Said } | null>(null);

  const go = (to: Screen) => {
    setDirection(SCREENS.indexOf(to) >= SCREENS.indexOf(screen) ? 'forward' : 'back');
    setScreen(to);
  };

  // An answer turns into its chip and the next question comes; the last one is where the tour ends.
  const answer = (question: Screen, id: string, card: HTMLElement | null, words?: string) => {
    setPicks((current) => ({ ...current, [question]: { id, words } }));
    if (question === LAST) return;
    if (card) setLift({ card: catchCard(card), chip: question });
    go(SCREENS[SCREENS.indexOf(question) + 1]);
  };

  const choose = (question: Screen, index: number) => {
    setTouched(true);
    const card = cardsOn(board.current, question)[index] ?? null;
    const picked = ANSWERS[question][index];
    // "Something else" opens its box over the cards instead of answering, as in the start flow.
    if (picked.ownWords && sayable(question)) {
      if (card) setOwn({ from: card.getBoundingClientRect(), question });
      return;
    }
    answer(question, picked.id, card);
  };

  const back = (to: Screen) => {
    setTouched(true);
    setOwn(null);
    go(to);
  };

  // Reduced motion: the tour's last frame, with no travel.
  useEffect(() => {
    if (!reducedMotion()) return;
    setStill(true);
    setPicks(ENDED);
    setScreen(LAST);
  }, []);

  // The tour picks with the same hands a click does; the latest of them, for the mark that calls it.
  const play = useRef<(at: number) => void>(() => undefined);
  play.current = (at) => {
    const leg = LEGS.find((l) => l.pick === at);
    if (touched || !leg) return;
    const index = ANSWERS[leg.screen].findIndex((a) => a.id === leg.card);
    answer(leg.screen, leg.card, cardsOn(board.current, leg.screen)[index] ?? null);
  };
  useEffect(() => play.current(step), [step]);

  // Each question's cards are measured once it is on screen, and again when the scene's width changes.
  useLayoutEffect(() => {
    const el = board.current;
    const frame = el?.closest<HTMLElement>('[role="group"]');
    if (!el || !frame) return undefined;
    const update = () => setSpots((current) => ({ ...current, [screen]: measure(el, screen) }));
    update();
    const resize = new ResizeObserver(update);
    resize.observe(frame);
    return () => resize.disconnect();
  }, [screen]);

  // Once its chip is on screen (the screen changes a render after the pick), the picked card turns into it.
  useLayoutEffect(() => {
    if (!lift) return;
    const chip = trail.current?.querySelector<HTMLElement>(`[data-glide="${lift.chip}"]`);
    if (chip) liftCard(lift.card, chip);
    setLift(null);
  }, [lift, screen]);

  const leg = [...LEGS].reverse().find((l) => step >= l.go) ?? null;
  const hovered = !touched && leg && step >= leg.on && step < leg.pick ? leg : null;
  // Until the next card is measured, the cursor waits where it last was.
  const legs = leg ? LEGS.slice(0, LEGS.indexOf(leg) + 1).reverse() : [];
  const at = legs.map((l) => spots[l.screen]?.[l.card]).find(Boolean) ?? ENTER_AT;
  const pressed = !touched && leg !== null && step === leg.press;
  // Each answer's chip shows from the question after it.
  const chips = SCREENS.slice(0, SCREENS.indexOf(screen)).flatMap((question) => {
    const pick = picks[question];
    const index = ANSWERS[question].findIndex((a) => a.id === pick?.id);
    return pick ? [{ question, shortcut: letterOf(index), label: pick.words ?? ANSWERS[question][index]?.title ?? '' }] : [];
  });

  const render = (shown: Screen, active: boolean) => {
    const pick = picks[shown];
    const picked = pick?.words && sayable(shown) ? OWN[shown] : (pick?.id ?? null);
    const open = active && own?.question === shown ? own : null;
    return (
      <section className={styles.screen} data-screen={active ? shown : undefined}>
        <p className={caseStyles.title}>{COPY[shown].title}</p>
        <div className={`${caseStyles.answers} ${open ? caseStyles.receded : ''} ${styles.grid} ${styles[shown]}`} data-grid>
          {ANSWERS[shown].map((answer, i) => (
            <OptionCard
              key={answer.id}
              visual={ART[answer.art]}
              title={answer.title}
              shortcut={letterOf(i)}
              selected={picked === answer.id}
              hovered={active && hovered?.screen === shown && hovered.card === answer.id}
              onSelect={active ? () => choose(shown, i) : undefined}
            />
          ))}
          {open ? (
            <OwnWords
              question={open.question}
              answers={ANSWERS[shown]}
              from={open.from}
              onClose={() => setOwn(null)}
              // Said in their own words: the chip shows them, as in the start flow.
              onSubmit={(words, sounds) => {
                setOwn(null);
                answer(open.question, sounds ?? OWN[open.question], null, words);
              }}
            />
          ) : null}
        </div>
      </section>
    );
  };

  return (
    <>
      <div ref={board} className={styles.board}>
        {/* Deck 2.0's tokens outside, the landing's inside: the inner ones win however the styles load. */}
        <div className={`${theme.theme} ${styles.appTheme}`}>
          <div className={`${landing.startTheme} ${styles.flow}`} data-motion-layer>
            <nav ref={trail} className={`${caseStyles.trail} ${styles.trail}`} aria-label="The answers so far">
              {chips.map((chip) => (
                <Chip key={chip.question} shortcut={chip.shortcut} label={chip.label} labelId={chip.question} onClick={() => back(chip.question)} />
              ))}
            </nav>
            <div className={styles.screens}>{still ? render(screen, true) : <ScreenSwap step={screen} direction={direction} render={render} />}</div>
          </div>
        </div>
      </div>
      <Cursor at={at} shown={!touched && step >= 1 && step < GONE} pressed={pressed} />
    </>
  );
}
